import { t } from "@lingui/macro";
import { useTasksFilterOptionsQuery } from "@src/__generated__/graphql";
import {
  TaskWhereColumn,
  useBudgetItemsFilterOptionsQuery,
  useGetProjectCategoriesQuery,
} from "@src/__generated__/urql-graphql";
import type { TTaskPageVariant } from "@src/components/modules/resource-planning/tasks/listing";
import { appStore } from "@src/stores/AppStore";
import { onError } from "@src/utils/apollo";
import { cannot } from "@src/utils/components/permissions";
import { useStore } from "@src/utils/hooks";
import { getParamFromQuery } from "@src/utils/url";
import { useRouter } from "next/router";
import { useEffect } from "react";

export function useTasksFiltersInit(variant: TTaskPageVariant) {
  const { tasksListingStore: store, workspaceStore } = useStore();
  const { query } = useRouter();

  useEffect(() => {
    if ("where" in query) return;
    const hideDone = store.filtersStore.where.filtersByColumn.get(
      TaskWhereColumn.HideDone,
    );
    hideDone?.setValue(!workspaceStore.settings?.show_done_tasks ? "true" : []);
  }, []);

  useEffect(() => {
    const search = getParamFromQuery("search", query) ?? "";
    store.filtersStore.searchTerm = search;
    store.filtersStore.where.fromURLSearchParam(query);
    store.filtersStore.orderBy.fromURLSearchParam(query);

    const brandFilter = store.filtersStore.where.filtersByColumn.get(
      TaskWhereColumn.BrandId,
    );
    const userFilter = store.filtersStore.where.filtersByColumn.get(
      TaskWhereColumn.UserId,
    );
    const teamFilter = store.filtersStore.where.filtersByColumn.get(
      TaskWhereColumn.TeamId,
    );
    const createdByFilter = store.filtersStore.where.filtersByColumn.get(
      TaskWhereColumn.CreatedByUserId,
    );
    const quickFilter = store.filtersStore.where.filtersByColumn.get(
      TaskWhereColumn.QuickFilter,
    );
    const projectFilter = store.filtersStore.where.filtersByColumn.get(
      TaskWhereColumn.ProjectId,
    );
    const projectCategoryFilter = store.filtersStore.where.filtersByColumn.get(
      TaskWhereColumn.ProjectCategoryId,
    );
    const projectTeamLeaderFilter =
      store.filtersStore.where.filtersByColumn.get(
        TaskWhereColumn.ProjectTeamLeaderId,
      );
    const projectManagerFilter = store.filtersStore.where.filtersByColumn.get(
      TaskWhereColumn.ProjectManagerId,
    );

    if (userFilter) {
      userFilter.clear();
      userFilter.hidden = false;
    }

    if (teamFilter) {
      teamFilter.hidden = false;
      teamFilter.clear();
    }

    if (createdByFilter) {
      createdByFilter.hidden = false;
      createdByFilter.clear();
    }

    quickFilter?.clear();

    if (projectFilter) {
      projectFilter.hidden = false;
    }
    if (brandFilter) {
      brandFilter.hidden = false;
    }

    if (projectCategoryFilter) {
      projectCategoryFilter.hidden = cannot("projectCategory_query");
    }

    if (projectTeamLeaderFilter) {
      projectTeamLeaderFilter.hidden = false;
    }

    if (projectManagerFilter) {
      projectManagerFilter.hidden = false;
    }

    const myTasksFilter = store.filtersStore.where.filtersByColumn.get(
      TaskWhereColumn.MyTasks,
    );
    myTasksFilter?.clear();

    const myCreatedTasksFilter = store.filtersStore.where.filtersByColumn.get(
      TaskWhereColumn.MyCreatedTasks,
    );
    myCreatedTasksFilter?.clear();

    const myWatchedTasksFilter = store.filtersStore.where.filtersByColumn.get(
      TaskWhereColumn.MyWatchedTasks,
    );
    myWatchedTasksFilter?.clear();

    const starredTasksFilter = store.filtersStore.where.filtersByColumn.get(
      TaskWhereColumn.StarredTasks,
    );
    starredTasksFilter?.clear();

    const onlyParentFilter = store.filtersStore.where.filtersByColumn.get(
      TaskWhereColumn.OnlyParent,
    );

    if (search !== "") {
      onlyParentFilter?.setValue("false");
    } else {
      onlyParentFilter?.setValue("true");
    }

    switch (variant) {
      case "my":
        userFilter?.hide();
        teamFilter?.hide();
        createdByFilter?.hide();
        myTasksFilter?.setValue("true");

        store.filtersStore.where.filtersByColumn
          .get(TaskWhereColumn.OnlyParent)
          ?.setValue(appStore.authStore.isInternalUser ? "true" : []);

        break;
      case "my-created":
        userFilter?.hide();
        teamFilter?.hide();
        createdByFilter?.hide();
        myCreatedTasksFilter?.setValue("true");
        break;
      case "starred":
        starredTasksFilter?.setValue("true");
        break;
      case "my-watched":
        myWatchedTasksFilter?.setValue("true");
        break;
      case "pinned":
        quickFilter?.setValue("pinned");
        break;
      case "project":
        store.filtersStore.where.reset([
          TaskWhereColumn.HideDone,
          TaskWhereColumn.OnlyParent,
        ]);
        projectFilter?.hide();
        teamFilter?.hide();
        brandFilter?.hide();
        projectTeamLeaderFilter?.hide();
        projectManagerFilter?.hide();

        if ("id" in query && query.id?.length) {
          const id = query.id;
          projectFilter?.setValue(Array.isArray(id) ? id[0] : id);
        }
        break;
    }

    return () => {
      store.filtersStore.searchTerm = "";

      if (variant !== "project") return;
      store.filtersStore.where.reset([
        TaskWhereColumn.HideDone,
        TaskWhereColumn.OnlyParent,
      ]);
    };
  }, [variant]);
}

export function useTasksFiltersOptions() {
  const { tasksListingStore: store } = useStore();

  useEffect(() => {
    if (cannot("projectCategory_query")) return;
    fetchCategories();
  }, []);

  useTasksFilterOptionsQuery({
    onCompleted(data) {
      store.filtersStore.setFilterOptions(data);
    },
    ...onError(),
  });

  const [{ data: categories }, fetchCategories] = useGetProjectCategoriesQuery({
    pause: true,
  });

  useEffect(() => {
    if (!categories?.projectCategories) return;
    store.filtersStore.where.filtersByColumn
      .get(TaskWhereColumn.ProjectCategoryId)
      ?.setOptions([]);

    categories.projectCategories.forEach(({ id, name }) => {
      store.filtersStore.where.filtersByColumn
        .get(TaskWhereColumn.ProjectCategoryId)
        ?.options.push({
          label: name,
          value: id,
        });
    });
  }, [categories]);

  const [{ data: budgetItemsOptionsData }, fetchBudgetItemsOptions] =
    useBudgetItemsFilterOptionsQuery({
      pause: store.filtersStore.selectedProjectsFilterValue.length === 0,
      variables: {
        project_id: store.filtersStore.selectedProjectsFilterValue,
      },
    });

  useEffect(() => {
    if (store.filtersStore.selectedProjectsFilterValue.length === 0) {
      store.filtersStore.budgetItemsFilter?.setOptions([]);
      store.filtersStore.budgetItemsFilter?.setNoOptionsMessage(
        t`Please select project first`,
      );
    } else {
      fetchBudgetItemsOptions();
      store.filtersStore.budgetItemsFilter?.setNoOptionsMessage(t`No options`);
    }
  }, [store.filtersStore.selectedProjectsFilterValue]);

  useEffect(() => {
    if (!budgetItemsOptionsData?.ourWorkBudgetItemsSimpleMap) return;
    store.filtersStore.budgetItemsFilter?.setOptions([]);

    const owbiCount = budgetItemsOptionsData.ourWorkBudgetItemsSimpleMap.length;
    for (let i = 0; i < owbiCount; i++) {
      const option = budgetItemsOptionsData.ourWorkBudgetItemsSimpleMap[i];
      store.filtersStore.budgetItemsFilter?.options.push({
        value: option.id,
        label: `${option.item_code} ${option.title}`,
      });
    }
  }, [budgetItemsOptionsData]);
}
